// Card #2499 -- the Screen's declared aspect ratio, as the board and the
// remote's preview understand it.
//
// `Screen.aspectRatio` is a closed enum (#2476; score-api screen.js
// ASPECT_RATIOS; AC1's enum/default/reject-on-write is proven by 2476's own
// score-api tests: __tests__/screen-handlers.test.js and
// __tests__/screen-public-resolver.test.js), declared when the display is racked. This table maps each
// value to its design-canvas size. All three keep 1920 or 1080 as one edge so
// the existing measured vw/vh-in-px numbers stay meaningful along at least
// one axis.
//
// The board's resolution order (ScoreboardFrame):
//   1. a declared ratio (the shell's ?aspectRatio= param, from the Screen it
//      resolved through) wins, even against the real viewport: the mismatch
//      letterboxes, silently. A declared-but-unknown/malformed value is 16:9.
//   2. no declared ratio (the legacy /scoreboard/:showId/:year[/:slug] route
//      and the ?target= shell mode, no Screen): the real viewport's own ratio,
//      snapped to 16:9 or 2:1 ONLY, exactly as before this card (card 2640's
//      two-entry table). 4:3 is reachable only by DECLARATION: snapping an
//      undeclared board to it would change displays already in the field
//      (driver decision on card 2499, 2026-10-01).
//
// A declared 4:3 canvas (1440x1080) renders the existing 16:9 layout
// letterboxed inside it (`layout`, scaled to fit and centred): the layout
// families are tuned for a 1920x1080 design size (RankedListLayout.css is a
// fixed 1804.8px wide), so nothing clips. Card 2500 tuned the ranked-list
// family for the 4:3 canvas itself (RankedListLayout.css, @container blocks),
// and card #3110 did the same for class-summary detailed
// (ClassSummaryLayout.css), and card #2501 for the single-entry family
// (SingleEntryLayout.css) and Mid-Ride Simple, so those fill the canvas and
// reclaim the bars (`fillCanvas`, boardRenderState.js boardFillsCanvas). Card
// #2502 added Message and Blank (every Blank, fallbacks included). Only the
// Image family and class-summary simple are still letterboxed here.

export const DEFAULT_ASPECT_RATIO = "16:9";

export const ASPECT_RATIO_CANVASES = {
  "2:1": { width: 1920, height: 960 },
  "16:9": { width: 1920, height: 1080 },
  // `layout`: the design size the layout families are tuned for, when it
  // differs from the canvas. Absent means the layout fills the canvas.
  "4:3": { width: 1440, height: 1080, layout: { width: 1920, height: 1080 } },
};

// The shapes an UNDECLARED board may snap to (the pre-2499 table). Not 4:3.
const SNAP_ASPECT_RATIOS = ["16:9", "2:1"];

export const ASPECT_RATIOS = Object.keys(ASPECT_RATIO_CANVASES);

export function isAspectRatio(value) {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(ASPECT_RATIO_CANVASES, value);
}

// A DECLARED value: valid stays, anything else (unknown, malformed, wrong
// type) is the 16:9 default. Never throws: the board never shows an error.
export function declaredAspectRatioOrDefault(value) {
  return isAspectRatio(value) ? value : DEFAULT_ASPECT_RATIO;
}

// "4:3" -> "4 / 3", the CSS `aspect-ratio` spelling (the remote's preview).
export function aspectRatioToCss(value) {
  return declaredAspectRatioOrDefault(value).replace(":", " / ");
}

// Snaps a measured box to the nearest UNDECLARED shape (16:9 or 2:1) by comparing ratios, not
// pixel counts (a 2:1 box of any physical size snaps the same way). A
// zero/unmeasured box is the 16:9 default, same "correct at the reference
// size" reasoning ScoreboardFrame's `?? 1` scale fallback uses.
export function snapViewportToAspectRatio(width, height) {
  if (!width || !height) return DEFAULT_ASPECT_RATIO;
  const viewportRatio = width / height;
  let best = DEFAULT_ASPECT_RATIO;
  let bestDelta = Infinity;
  for (const key of SNAP_ASPECT_RATIOS) {
    const { width: w, height: h } = ASPECT_RATIO_CANVASES[key];
    const delta = Math.abs(viewportRatio - w / h);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = key;
    }
  }
  return best;
}

// The canvas for a measured box and an optional declared ratio.
// `declared` null/undefined means "no Screen": snap from the box.
// `letterbox` is null when the layout fills the canvas, otherwise where the
// scaled-to-fit layout sits inside the canvas (a declared 4:3).
// Card #2500: `fillCanvas` is true for a layout family that was tuned for the
// canvas shape itself (the ranked-list family, class-summary detailed from
// #3110, the single-entry family and Mid-Ride Simple from #2501, Message and
// every Blank from #2502), so it fills a declared 4:3 canvas instead of sitting
// letterboxed in it. The Image family and class-summary simple leave it false
// and are letterboxed exactly as before.
export function resolveCanvasSize(boxWidth, boxHeight, declared, fillCanvas = false) {
  const key =
    declared === null || declared === undefined
      ? snapViewportToAspectRatio(boxWidth, boxHeight)
      : declaredAspectRatioOrDefault(declared);
  const { width, height, layout } = ASPECT_RATIO_CANVASES[key];
  let letterbox = null;
  if (layout && !fillCanvas) {
    const scale = Math.min(width / layout.width, height / layout.height);
    letterbox = {
      width: layout.width,
      height: layout.height,
      scale,
      left: (width - layout.width * scale) / 2,
      top: (height - layout.height * scale) / 2,
    };
  }
  return { key, width, height, letterbox };
}
